import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime, hearingType } from "@/lib/api";
import { PageHeader, EmptyState, Badge } from "@/components/Shared";

export default function Proceedings() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/proceedings").then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const now = new Date();
  const upcoming = rows.filter((r) => new Date(r.scheduled_at) >= now).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const past = rows.filter((r) => new Date(r.scheduled_at) < now);

  const Table = ({ items, testId }) => (
    <div className="border border-border bg-card overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm min-w-[820px]">
        <thead>
          <tr className="border-b border-border bg-muted/60">
            {["Date & time", "Case", "Hearing type", "Purpose", "Mode", "Status", "Daily order", "Next date"].map((h) => (
              <th key={h} className="p-4 text-left label-caps font-normal">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id} className="border-b border-border last:border-0 hover:bg-accent transition-colors duration-200" data-testid={`proceeding-list-row-${p.id}`}>
              <td className="p-4 whitespace-nowrap">{fmtDateTime(p.scheduled_at)}</td>
              <td className="p-4">
                <Link to={`/cases/${p.case_id}`} className="font-mono text-xs text-blue-900 dark:text-blue-400 hover:underline">
                  {p.case_number !== "—" ? p.case_number : p.ref_id}
                </Link>
                <p className="text-xs text-muted-foreground mt-1">{p.case_title}</p>
              </td>
              <td className="p-4"><Badge className={hearingType(p.hearing_type).cls} data-testid={`cause-type-${p.id}`}>{hearingType(p.hearing_type).label}</Badge></td>
              <td className="p-4 font-medium">{p.purpose}</td>
              <td className="p-4 text-muted-foreground capitalize">{p.mode}</td>
              <td className="p-4"><Badge className="bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200">{p.status}</Badge></td>
              <td className="p-4 text-muted-foreground max-w-[280px] truncate">{p.daily_order || "—"}</td>
              <td className="p-4 text-muted-foreground whitespace-nowrap">{fmtDate(p.next_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div data-testid="proceedings-page">
      <PageHeader
        eyebrow="Cause list"
        title="Proceedings & Daily Orders"
        description="Every sitting posted in your references, with the order passed on each date."
      />
      {rows.length === 0 ? (
        <EmptyState testId="proceedings-empty" title="No sittings posted" hint="The arbitrator posts dates from the case file." />
      ) : (
        <div className="space-y-10">
          <div>
            <p className="label-caps mb-4">Upcoming ({upcoming.length})</p>
            {upcoming.length ? <Table items={upcoming} testId="upcoming-proceedings-table" />
              : <p className="text-sm text-muted-foreground">No upcoming sittings.</p>}
          </div>
          <div>
            <p className="label-caps mb-4">Past ({past.length})</p>
            {past.length ? <Table items={past} testId="past-proceedings-table" />
              : <p className="text-sm text-muted-foreground">No past sittings.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
